import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { formatPhoneNumber } from '../common/formatting/phoneFormat';
import { formatDateStringWithTime } from '../common/formatting/dateFormat';
import {
  Box,
  Button,
  ColumnLayout,
  CopyToClipboard,
  Header,
  Popover,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import type { StatusIndicatorProps } from '@cloudscape-design/components';
import type { PhoneLineTypeInfo } from '../common/types';

export type PhoneNumberProps = {
  phoneNumber?: string | null;
  lineTypeInfo?: PhoneLineTypeInfo | null;
  showLineTypeInfo?: boolean;
};

type PhoneLineTypeState = {
  label: string;
  type: StatusIndicatorProps.Type;
  lineTypeLabel: string;
  rawLineType?: string | null;
  lookupStatusLabel: string;
  noStoredLookup: boolean;
};

function humanize(value?: string | null): string {
  if (!value) return 'Unknown';
  const cleaned = value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : 'Unknown';
}

function getLineTypeState(lineTypeInfo?: PhoneLineTypeInfo | null): PhoneLineTypeState {
  const rawLineType = lineTypeInfo?.line_type ?? null;
  const rawLookupStatus = lineTypeInfo?.lookup_status ?? null;
  const lookupStatus = rawLookupStatus?.toLowerCase() ?? null;
  const normalizedLineType = rawLineType?.toLowerCase() ?? null;
  const validationErrors = lineTypeInfo?.validation_errors ?? [];

  const base = {
    rawLineType,
    lookupStatusLabel: rawLookupStatus ? humanize(rawLookupStatus) : 'No stored lookup status',
    noStoredLookup: !lineTypeInfo || !rawLookupStatus || lookupStatus === 'not_requested',
  };

  if (lineTypeInfo?.is_valid === false || validationErrors.length > 0) {
    return {
      ...base,
      label: 'Invalid',
      type: 'error',
      lineTypeLabel: rawLineType ? humanize(rawLineType) : 'Unknown',
    };
  }

  if (
    lookupStatus === 'failed' ||
    lookupStatus === 'error' ||
    lookupStatus === 'lookup_failed' ||
    Boolean(lineTypeInfo?.line_type_error_code)
  ) {
    return {
      ...base,
      label: 'Lookup failed',
      type: 'error',
      lineTypeLabel: rawLineType ? humanize(rawLineType) : 'Unknown',
      noStoredLookup: false,
    };
  }

  if (!lineTypeInfo || !rawLookupStatus || lookupStatus === 'not_requested') {
    return {
      ...base,
      label: 'Not verified',
      type: 'pending',
      lineTypeLabel: 'Unknown',
    };
  }

  if (!normalizedLineType || normalizedLineType === 'unknown') {
    return {
      ...base,
      label: 'Unknown',
      type: 'info',
      lineTypeLabel: 'Unknown',
    };
  }

  if (normalizedLineType.includes('voip')) {
    return {
      ...base,
      label: 'VoIP',
      type: 'warning',
      lineTypeLabel: 'VoIP',
    };
  }

  if (normalizedLineType.includes('mobile') || normalizedLineType.includes('cell')) {
    return {
      ...base,
      label: 'Mobile',
      type: 'success',
      lineTypeLabel: 'Mobile',
    };
  }

  if (
    normalizedLineType.includes('landline') ||
    normalizedLineType.includes('fixed') ||
    normalizedLineType.includes('wireline')
  ) {
    return {
      ...base,
      label: 'Landline',
      type: 'info',
      lineTypeLabel: 'Landline',
    };
  }

  return {
    ...base,
    label: 'Unknown',
    type: 'info',
    lineTypeLabel: humanize(rawLineType),
  };
}

function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box variant="span">{children}</Box>
    </div>
  );
}

function PhoneLineTypeDetails({
  phoneNumber,
  lineTypeInfo,
  state,
}: {
  phoneNumber: string;
  lineTypeInfo?: PhoneLineTypeInfo | null;
  state: PhoneLineTypeState;
}) {
  const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
  const normalized = lineTypeInfo?.e164_phone_number ?? lineTypeInfo?.display_phone_number ?? null;
  const validationErrors = lineTypeInfo?.validation_errors ?? [];
  const source = [lineTypeInfo?.source_model, lineTypeInfo?.source_field].filter(Boolean).join('.');
  const rawLineType =
    state.rawLineType && humanize(state.rawLineType) !== state.lineTypeLabel
      ? state.rawLineType
      : null;
  const validityLabel =
    lineTypeInfo?.is_valid === true
      ? 'Valid'
      : lineTypeInfo?.is_valid === false
        ? 'Invalid'
        : 'Unknown';

  return (
    <div style={{ maxWidth: '360px', minWidth: 'min(320px, calc(100vw - 4rem))' }}>
      <SpaceBetween direction="vertical" size="s">
        <Header variant="h3" description={`Stored lookup metadata for ${formattedPhoneNumber}`}>
          Phone lookup
        </Header>

        <DetailField label="Lookup result">
          <StatusIndicator nativeAttributes={{ 'aria-label': state.label }} type={state.type}>
            {state.label}
          </StatusIndicator>
        </DetailField>

        <ColumnLayout columns={2} variant="text-grid">
          <DetailField label="Line type">{state.lineTypeLabel}</DetailField>
          <DetailField label="Lookup status">{state.lookupStatusLabel}</DetailField>
          <DetailField label="Validity">{validityLabel}</DetailField>
          {lineTypeInfo?.carrier_name && (
            <DetailField label="Carrier">{lineTypeInfo.carrier_name}</DetailField>
          )}
          {lineTypeInfo?.twilio_lookup_last_checked_at && (
            <DetailField label="Last checked">
              {formatDateStringWithTime(lineTypeInfo.twilio_lookup_last_checked_at)}
            </DetailField>
          )}
          {source && <DetailField label="Source">{source}</DetailField>}
          {normalized && normalized !== phoneNumber && normalized !== formattedPhoneNumber && (
            <DetailField label="E.164">{normalized}</DetailField>
          )}
          {lineTypeInfo?.national_format && lineTypeInfo.national_format !== formattedPhoneNumber && (
            <DetailField label="National format">{lineTypeInfo.national_format}</DetailField>
          )}
          {rawLineType && <DetailField label="Stored line type">{rawLineType}</DetailField>}
          {lineTypeInfo?.line_type_error_code && (
            <DetailField label="Error code">{lineTypeInfo.line_type_error_code}</DetailField>
          )}
        </ColumnLayout>

        {state.noStoredLookup && (
          <Box variant="small" color="text-body-secondary">
            No stored Twilio lookup yet. This number is not verified.
          </Box>
        )}
        {validationErrors.length > 0 && (
          <Box variant="small" color="text-status-error">
            Validation errors: {validationErrors.join(', ')}
          </Box>
        )}
      </SpaceBetween>
    </div>
  );
}

function PhoneLineTypePopover({
  phoneNumber,
  formattedPhoneNumber,
  lineTypeInfo,
}: {
  phoneNumber: string;
  formattedPhoneNumber: string;
  lineTypeInfo?: PhoneLineTypeInfo | null;
}) {
  const state = getLineTypeState(lineTypeInfo);

  return (
    <SpaceBetween direction="horizontal" size="xxxs" alignItems="center">
      <Popover
        content={<PhoneLineTypeDetails phoneNumber={phoneNumber} lineTypeInfo={lineTypeInfo} state={state} />}
        dismissButton={false}
        position="top"
        size="medium"
        triggerType="custom"
      >
        <Button
          ariaLabel={`Phone lookup details for ${formattedPhoneNumber}`}
          iconAlign="right"
          iconName="status-info"
          variant="inline-link"
          wrapText={false}
        >
          {formattedPhoneNumber}
        </Button>
      </Popover>
      <CopyToClipboard
        copyButtonAriaLabel="Copy phone number"
        copyErrorText="Phone number failed to copy"
        copySuccessText="Phone number copied"
        textToCopy={formattedPhoneNumber}
        variant="icon"
      />
    </SpaceBetween>
  );
}

export function PhoneNumber(props: PhoneNumberProps) {
  const { phoneNumber, lineTypeInfo, showLineTypeInfo } = props;

  const formattedPhoneNumber = useMemo(() => {
    return `${phoneNumber ? formatPhoneNumber(phoneNumber) : '-'}`;
  }, [phoneNumber]);

  if (!phoneNumber) {
    return <Box variant="span">{formattedPhoneNumber}</Box>;
  }

  if (showLineTypeInfo) {
    return (
      <PhoneLineTypePopover
        phoneNumber={phoneNumber}
        formattedPhoneNumber={formattedPhoneNumber}
        lineTypeInfo={lineTypeInfo}
      />
    );
  }

  return (
    <CopyToClipboard
      copyButtonAriaLabel="Copy phone number"
      copyErrorText="Phone number failed to copy"
      copySuccessText="Phone number copied"
      textToCopy={formattedPhoneNumber}
      variant="inline"
    />
  );
}
